import { SAVE_VERSION } from "@/config";
import type { Lang } from "@/math";
import { scheduleCloudUpload } from "@/services/cloud";
import {
  currentProfileId,
  listProfiles,
  newProfileId,
  readProfileSave,
  removeProfile,
  writeProfileSave,
  type ProfileMeta,
} from "./profiles";
import { defaultHero, hero, lang, settings, type HeroState, type Settings } from "./store";

export interface SaveFile {
  version: number;
  savedAt: string;
  hero: HeroState;
  lang: Lang;
  settings: Settings;
}

/** Runtime data owned by the World scene that the save needs (player position). */
export const worldRuntime: { pos?: { x: number; y: number } } = {};

export function hasSave(): boolean {
  return listProfiles().length > 0;
}

function metaFor(id: string, h: HeroState, savedAt: string): ProfileMeta {
  return { id, name: h.name, avatar: h.avatar, level: h.level, lvl: h.lvl, updatedAt: savedAt };
}

/** Save the current hero into its profile slot (creating the profile on first save). */
export function saveGame(): boolean {
  try {
    const id = currentProfileId.value ?? newProfileId();
    currentProfileId.value = id;
    const file: SaveFile = {
      version: SAVE_VERSION,
      savedAt: new Date().toISOString(),
      hero: { ...hero.value, pos: worldRuntime.pos ?? hero.value.pos },
      lang: lang.value,
      settings: settings.value,
    };
    const json = JSON.stringify(file);
    const meta = metaFor(id, file.hero, file.savedAt);
    writeProfileSave(id, json, meta);
    scheduleCloudUpload(id, json, meta);
    return true;
  } catch {
    return false;
  }
}

/** Load a profile into the game state. */
export function loadGame(id: string): boolean {
  try {
    const raw = readProfileSave(id);
    if (!raw) return false;
    const file = migrate(JSON.parse(raw) as Partial<SaveFile>);
    if (!file) return false;
    hero.value = file.hero;
    lang.value = file.lang;
    settings.value = { ...settings.value, ...file.settings };
    currentProfileId.value = id;
    worldRuntime.pos = file.hero.pos;
    return true;
  } catch {
    return false;
  }
}

/** Enabled topic ids stored in a profile's save (null = all). */
export function readProfileTopics(id: string): string[] | null {
  try {
    const raw = readProfileSave(id);
    if (!raw) return null;
    const file = migrate(JSON.parse(raw) as Partial<SaveFile>);
    return file?.hero.topics ?? null;
  } catch {
    return null;
  }
}

/** Change the enabled topics of a saved hero without loading it into the game. */
export function updateProfileTopics(id: string, topics: string[] | null): boolean {
  try {
    const raw = readProfileSave(id);
    if (!raw) return false;
    const file = migrate(JSON.parse(raw) as Partial<SaveFile>);
    if (!file) return false;
    const heroState = { ...file.hero };
    if (topics) heroState.topics = topics;
    else delete heroState.topics;
    const next: SaveFile = { ...file, hero: heroState, savedAt: new Date().toISOString() };
    const json = JSON.stringify(next);
    const meta = metaFor(id, heroState, next.savedAt);
    writeProfileSave(id, json, meta);
    scheduleCloudUpload(id, json, meta);
    if (currentProfileId.value === id) hero.value = heroState;
    return true;
  } catch {
    return false;
  }
}

/** Start a brand-new hero in a fresh profile slot. */
export function startNewGame(h: HeroState): void {
  hero.value = h;
  currentProfileId.value = newProfileId();
  worldRuntime.pos = undefined;
  saveGame();
}

export function deleteSave(id: string): void {
  removeProfile(id);
}

/** Store a save file received from the cloud into the local profile slot (used by sync). */
export function importSave(id: string, json: string): ProfileMeta | null {
  try {
    const file = migrate(JSON.parse(json) as Partial<SaveFile>);
    if (!file) return null;
    const meta = metaFor(id, file.hero, file.savedAt);
    writeProfileSave(id, json, meta);
    return meta;
  } catch {
    return null;
  }
}

/** Upgrade older save files to the current version. Returns null when the file is unusable. */
export function migrate(file: Partial<SaveFile>): SaveFile | null {
  if (!file.hero || typeof file.hero !== "object") return null;
  const version = file.version ?? 1;
  if (version > SAVE_VERSION) return null;
  const raw = file.hero as Partial<HeroState> & { potions?: number };
  const base = defaultHero();
  const heroState: HeroState = {
    ...base,
    ...raw,
    inventory: { ...(raw.inventory ?? {}) },
    equipment: { ...base.equipment, ...(raw.equipment ?? {}) },
    quests: { ...(raw.quests ?? {}) },
    flags: { ...(raw.flags ?? {}) },
    history: Array.isArray(raw.history) ? raw.history : [],
    avatar: raw.avatar === "girl" ? "girl" : "boy",
  };
  if (version < 2) {
    // v1 kept potions as a counter; the map layout also changed, so the saved position is dropped
    if (typeof raw.potions === "number" && raw.potions > 0)
      heroState.inventory.potion = (heroState.inventory.potion ?? 0) + raw.potions;
    delete heroState.pos;
  }
  if (!["sp4", "sp5", "sp6", "sp7", "sp8", "lo1", "lo2", "lo3", "lo4"].includes(heroState.level))
    heroState.level = "sp4";
  delete (heroState as { potions?: number }).potions;
  return {
    version: SAVE_VERSION,
    savedAt: file.savedAt ?? new Date().toISOString(),
    hero: heroState,
    lang: file.lang === "en" ? "en" : "pl",
    settings: { timerSeconds: 0, preference: "auto", calculator: true, ...(file.settings ?? {}) },
  };
}
